// gallery-ada.ts
// Helper utilities for Gallery ADA behavior: hide non-visible slides from screen readers
// and remove their descendants from the Tab order.

export const FOCUSABLE_SELECTOR =
    'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

export interface GalleryAdaOptions {
    /** If false, removes aria-hidden and restores saved tabindex for all slides */
    enabled: boolean;

    /** Attribute used to find slide roots (default: [data-index]) */
    slideSelector?: string;

    /** Attribute used to store previous tabindex (default: data-prev-tabindex) */
    prevTabindexAttr?: string;

    /** Also set aria-hidden on slides (default: true) */
    useAriaHidden?: boolean;
}

/**
 * Updates accessibility for gallery slides:
 * - Sets aria-hidden=true for non-visible slides (optional)
 * - Removes all focusable descendants of non-visible slides from Tab order
 * - Restores previous tabindex for visible slides
 */
export function applyGalleryAdaForVisibleRange(
    container: HTMLElement,
    startIndex: number,
    itemsPerSlide: number,
    options: GalleryAdaOptions
) {
    const {
        enabled,
        slideSelector = '[data-index]',
        prevTabindexAttr = 'data-prev-tabindex',
        useAriaHidden = true,
    } = options;

    const slides = Array.from(container.querySelectorAll<HTMLElement>(slideSelector));

    if (!enabled) {
        // Feature disabled: remove aria-hidden and restore tabindex for all slides
        for (const slide of slides) {
            if (useAriaHidden) slide.removeAttribute('aria-hidden');
            restoreTabIndex(slide, prevTabindexAttr);
        }
        return;
    }

    const endIndex = startIndex + itemsPerSlide - 1;

    for (const slide of slides) {
        const idx = Number((slide as any).dataset?.index ?? slide.getAttribute('data-index'));
        const isVisible = Number.isFinite(idx) && idx >= startIndex && idx <= endIndex;

        if (useAriaHidden) slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        if (!isVisible) {
            removeFromTabOrder(slide, prevTabindexAttr);
        } else {
            restoreTabIndex(slide, prevTabindexAttr);
        }
    }
}

function removeFromTabOrder(slide: HTMLElement, prevTabindexAttr: string) {
    const focusables = Array.from(slide.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    for (const el of focusables) {
        // Store previous tabindex once
        if (!el.hasAttribute(prevTabindexAttr)) {
            const prev = el.getAttribute('tabindex');
            el.setAttribute(prevTabindexAttr, prev === null ? '' : prev);
        }
        el.setAttribute('tabindex', '-1');
    }
}

function restoreTabIndex(slide: HTMLElement, prevTabindexAttr: string) {
    const focusables = Array.from(slide.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    for (const el of focusables) {
        const prev = el.getAttribute(prevTabindexAttr);
        if (prev !== null) {
            el.removeAttribute(prevTabindexAttr);
            if (prev === '') el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', prev);
        }
    }
}
